import React, { useState } from 'react';
import {
  Target, Rocket, Shield, Users, ArrowRight,
  CheckCircle, Award, TrendingUp, Star, Quote, UsersIcon,
  Clock, HandshakeIcon, ThumbsUp, Zap
} from 'lucide-react';
import { Link } from 'react-router-dom';

const aims = [
  {
    icon: Target,
    title: 'Customer‑First Approach',
    description: 'We design every product around your needs, ensuring transparency and empathy at every step.',
  },
  {
    icon: Rocket,
    title: 'Speed Without Compromise',
    description: 'Fast approvals and disbursements, paired with thorough risk assessment for your safety.',
  },
  {
    icon: Shield,
    title: 'Trust & Security',
    description: 'Your data is protected with bank‑grade security, and our processes are fully compliant.',
  },
  {
    icon: Users,
    title: 'Inclusive Growth',
    description: 'We empower individuals and small businesses to achieve their financial dreams, regardless of background.',
  },
];

const AboutCompany = () => {
  const [clickedIndex, setClickedIndex] = useState(null);

  const handleCardClick = (idx) => {
    setClickedIndex(idx);
    setTimeout(() => setClickedIndex(null), 300);
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-8px); }
            100% { transform: translateY(0px); }
          }
          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes subtleGlow {
            0% { border-color: rgba(34,197,94,0.3); box-shadow: 0 4px 14px rgba(34,197,94,0.1); }
            100% { border-color: rgba(234,179,8,0.6); box-shadow: 0 8px 25px rgba(234,179,8,0.2); }
          }
          @keyframes cardPop {
            0% { transform: scale(1); background-color: white; }
            50% { transform: scale(0.97); background-color: #fef9c3; box-shadow: 0 0 0 2px rgba(234,179,8,0.5); }
            100% { transform: scale(1); background-color: white; }
          }
          .animate-card-in {
            opacity: 0;
            animation: fadeInUp 0.6s cubic-bezier(0.2,0.9,0.4,1.1) forwards;
          }
          .animate-float {
            animation: float 5s ease-in-out infinite;
          }
          .animate-gradient-bg {
            background: linear-gradient(270deg, #fef9c3, #dcfce7, #fef9c3, #bbf7d0);
            background-size: 300% 300%;
            animation: gradientShift 8s ease infinite;
          }
          .btn-pulse:hover {
            animation: gentlePulse 1s infinite;
          }
          @keyframes gentlePulse {
            0% { box-shadow: 0 0 0 0 rgba(34,197,94,0.3); }
            70% { box-shadow: 0 0 0 10px rgba(34,197,94,0); }
            100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); }
          }
          .icon-hover {
            transition: all 0.3s ease;
          }
          .group:hover .icon-hover {
            transform: scale(1.1) rotate(3deg);
            color: #eab308;
          }
        `}
      </style>
      <section className="py-20 w-full relative overflow-hidden bg-gradient-to-br from-green-100 via-yellow-50/40 to-white">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -left-20 w-72 h-72 bg-green-200/30 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-yellow-200/30 rounded-full blur-3xl animate-float [animation-delay:-2s]" />
          <div className="absolute top-1/3 right-1/4 w-48 h-48 bg-green-300/20 rounded-full blur-2xl animate-float [animation-delay:-4s]" />
        </div>
        <div className="w-full px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">
            <div className="animate-card-in h-full">
              <div className="bg-green-700 rounded-2xl shadow-xl p-6 md:p-8 h-full flex flex-col justify-between text-white">
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="px-4 py-1.5 bg-gradient-to-r from-green-400 to-yellow-500 text-green-900 text-sm font-bold rounded-full shadow-md flex items-center gap-1">
                      <Award className="w-4 h-4" /> ABOUT COMPANY
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-green-300 font-medium mb-4">
                    <span className="w-8 h-px bg-green-400"></span>
                  </div>
                  <h2 className="text-4xl sm:text-5xl font-bold leading-tight mb-4">
                    <span className="text-white"> CashPro Financial Services</span>
                  </h2>
                  <p className="text-green-100 text-lg leading-relaxed border-l-4 border-yellow-400 pl-4 mb-4">
                    At CashPro Financial Services, we believe financial support should be tailored to your ambitions—not limited by rigid processes. With years of expertise and a customer-first approach, we provide smart, reliable, and fast financial solutions that empower individuals and businesses to grow with confidence.
                  </p>

                  <p className="text-green-100 text-md leading-relaxed mb-6">
                    From personal needs to business expansion, our goal is simple—make funding easy, accessible, and stress-free. With minimal documentation, competitive interest rates, and transparent processes, CashPro Financial Services stands as your trusted financial partner at every step of your journey.
                  </p>
                  <div className="space-y-2 mb-6">
                    <div className="flex items-start gap-2 group">
                      <CheckCircle className="w-5 h-5 text-green-300 mt-0.5 group-hover:scale-110 transition-transform" />
                      <span className="text-green-100">100% digital process, zero paperwork hassle</span>
                    </div>
                    <div className="flex items-start gap-2 group">
                      <CheckCircle className="w-5 h-5 text-green-300 mt-0.5 group-hover:scale-110 transition-transform" />
                      <span className="text-green-100">Personalized interest rates based on profile</span>
                    </div>
                    <div className="flex items-start gap-2 group">
                      <CheckCircle className="w-5 h-5 text-green-300 mt-0.5 group-hover:scale-110 transition-transform" />
                      <span className="text-green-100">Dedicated relationship manager for every client</span>
                    </div>
                  </div>
                  <div>
                    <Link
                      to="/contact"
                      className="btn-pulse inline-flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-500 hover:to-green-400 text-green-900 font-semibold px-8 py-3 rounded-lg transition-all duration-300 hover:gap-4 group shadow-md hover:shadow-xl"
                    >
                      Learn More
                      <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            <div className="animate-card-in h-full bg-green-50 p-6 md:p-8 text-green-900 flex flex-col justify-between">
              <div>
                <div className="mb-8 text-center lg:text-left">
                  <h3 className="text-2xl sm:text-3xl font-bold text-green-800 inline-flex items-center gap-2">
                    <Target className="w-7 h-7 text-green-600 animate-float" />
                    Our Company Aim
                  </h3>
                  <div className="w-24 h-1 bg-gradient-to-r from-green-500 to-yellow-500 mt-2 rounded-full mx-auto lg:mx-0"></div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                  {aims.map((aim, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleCardClick(idx)}
                      className={`group cursor-pointer transition-all duration-300 rounded-xl p-5 hover:bg-white/40 ${
                        clickedIndex === idx ? 'card-clicked' : ''
                      }`}
                      style={{ animationDelay: `${idx * 0.08}s` }}
                    >
                      <div className="flex flex-col items-center text-center gap-3">
                        <div className="p-3 bg-white/60 rounded-full group-hover:scale-110 transition-all duration-300 shadow-sm">
                          <aim.icon className="w-7 h-7 text-green-700 group-hover:text-yellow-600 icon-hover" />
                        </div>
                        <h4 className="font-bold text-green-800 text-lg group-hover:text-green-700 transition-colors">
                          {aim.title}
                        </h4>
                        <p className="text-sm text-green-700 leading-relaxed group-hover:text-green-800">
                          {aim.description}
                        </p>
                      </div>
                      <div className="mt-4 h-0.5 w-0 bg-gradient-to-r from-green-500 to-yellow-500 group-hover:w-full transition-all duration-500 rounded-full mx-auto" />
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-8 text-center">
                <p className="text-sm text-green-700 flex items-center justify-center gap-1">
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-[0] pointer-events-none opacity-20">
          <svg className="relative block w-full h-12" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" fill="#22c55e" opacity="0.3" />
            <path d="M0,0V15.81C13,21.25,27.93,25.67,44.24,28.45c69.76,10.19,140.09,9.32,209.86-2.41,60.48-10.14,119.18-30.6,179.6-40.67,60.13-10,120.86-6.68,180.66,4.77,59.77,11.45,118.6,31.75,178.23,41.69,59.54,9.94,119.47,9.54,178.84-1.21,49.31-8.94,96.9-27.18,144.11-46.23V0Z" fill="#eab308" opacity="0.2" />
          </svg>
        </div>
      </section>
    </>
  );
};

export default AboutCompany;